import React, { useState, useEffect, useMemo } from "react";
import { useLocation as useRouterLocation } from "react-router-dom";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "../../../components/ui/sheet";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import { createFeedback, createReport } from "wasp/client/operations";
import { useToast } from "../../../hooks/use-toast";
import { AlertCircle, CheckCircle2, MessageSquare, Flag } from "lucide-react";

interface ReportingSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type ReportType = "FEEDBACK" | "POST" | "LOCATION" | "SECTION";

export function ReportingSheet({ open, onOpenChange }: ReportingSheetProps) {
    const routerLocation = useRouterLocation();
    const { selectedLocation } = useLocationContext();
    const { toast } = useToast();

    const [reportType, setReportType] = useState<ReportType>("FEEDBACK");
    const [reason, setReason] = useState("");
    const [details, setDetails] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Auto-detect context
    const context = useMemo(() => {
        const path = routerLocation.pathname;

        // Check for post ID in URL (usually the last segment if it matches uuid/cuid pattern)
        const postMatch = path.match(/\/(?:events|notices|buy-sell|food-court|lost-found|request-offer|carpooling|accommodation|textbooks|immediate-help|ask-for-help|share-with-neighbors|other-requests)\/([a-zA-Z0-9-]+)$/);
        const postId = postMatch ? postMatch[1] : null;

        // Check for section in URL
        const sectionMatch = path.match(/\/locations\/[^/]+\/([^/]+)/);
        const sectionKey = sectionMatch ? sectionMatch[1] : null;

        return {
            postId,
            sectionKey,
            locationId: selectedLocation?.id,
            locationName: selectedLocation?.name,
            pageUrl: window.location.href
        };
    }, [routerLocation.pathname, selectedLocation]);

    // Set initial report type based on context
    useEffect(() => {
        if (open) {
            if (context.postId) {
                setReportType("POST");
            } else if (context.sectionKey) {
                setReportType("SECTION");
            } else if (context.locationId) {
                setReportType("LOCATION");
            } else {
                setReportType("FEEDBACK");
            }
        }
    }, [open, context]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (reportType === "FEEDBACK") {
                await createFeedback({
                    content: details,
                    locationId: context.locationId,
                    pageUrl: context.pageUrl,
                });
                toast({
                    title: "Feedback submitted",
                    description: "Thank you for your feedback!",
                });
            } else {
                await createReport({
                    targetType: reportType as any,
                    targetId: reportType === "POST" ? context.postId! : context.locationId!,
                    sectionKey: reportType === "SECTION" ? context.sectionKey! : undefined,
                    reason,
                    details,
                    locationId: context.locationId,
                    contentId: reportType === "POST" ? context.postId! : undefined,
                });
                toast({
                    title: "Report submitted",
                    description: "We've received your report and will review it soon.",
                });
            }
            onOpenChange(false);
            // Reset form
            setReason("");
            setDetails("");
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error submitting",
                description: error.message || "Something went wrong.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
                <SheetHeader className="mb-6">
                    <SheetTitle className="flex items-center gap-2 text-2xl">
                        {reportType === "FEEDBACK" ? (
                            <MessageSquare className="h-6 w-6 text-primary" />
                        ) : (
                            <Flag className="h-6 w-6 text-destructive" />
                        )}
                        {reportType === "FEEDBACK" ? "Provide Feedback" : "Report an Issue"}
                    </SheetTitle>
                    <SheetDescription>
                        {reportType === "FEEDBACK"
                            ? "Share your thoughts or suggestions with us."
                            : "Let us know if something is wrong so we can fix it."}
                    </SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="reportType">What would you like to do?</Label>
                        <Select
                            value={reportType}
                            onValueChange={(val) => setReportType(val as ReportType)}
                        >
                            <SelectTrigger id="reportType">
                                <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="FEEDBACK">Generic Feedback</SelectItem>
                                <SelectItem value="POST" disabled={!context.postId}>
                                    Report this Post
                                </SelectItem>
                                <SelectItem value="SECTION" disabled={!context.sectionKey}>
                                    Report Section as Not Applicable
                                </SelectItem>
                                <SelectItem value="LOCATION" disabled={!context.locationId}>
                                    Report this Location
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {reportType !== "FEEDBACK" && (
                        <div className="space-y-2">
                            <Label htmlFor="reason">Reason for reporting</Label>
                            <Select value={reason} onValueChange={setReason} required>
                                <SelectTrigger id="reason">
                                    <SelectValue placeholder="Select a reason" />
                                </SelectTrigger>
                                <SelectContent>
                                    {reportType === "POST" ? (
                                        <>
                                            <SelectItem value="spam">Spam / Misleading</SelectItem>
                                            <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                                            <SelectItem value="duplicate">Duplicate post</SelectItem>
                                            <SelectItem value="expired">Already sold / expired</SelectItem>
                                            <SelectItem value="other">Other</SelectItem>
                                        </>
                                    ) : reportType === "SECTION" ? (
                                        <>
                                            <SelectItem value="not_applicable">Not applicable to this location</SelectItem>
                                            <SelectItem value="redundant">Redundant for this location</SelectItem>
                                            <SelectItem value="misplaced">Misplaced section</SelectItem>
                                        </>
                                    ) : (
                                        <>
                                            <SelectItem value="incorrect_info">Incorrect information</SelectItem>
                                            <SelectItem value="obsolete">No longer exists</SelectItem>
                                            <SelectItem value="duplicate_location">Duplicate location</SelectItem>
                                            <SelectItem value="other">Other</SelectItem>
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="details">
                            {reportType === "FEEDBACK" ? "Your Feedback" : "Additional Details"}
                        </Label>
                        <Textarea
                            id="details"
                            placeholder={
                                reportType === "FEEDBACK"
                                    ? "What's on your mind?..."
                                    : "Please provide more context..."
                            }
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            className="min-h-[120px]"
                        />
                    </div>

                    <div className="bg-muted/50 p-3 rounded-lg text-xs text-muted-foreground space-y-1">
                        <p className="font-semibold text-foreground/70">Context Info:</p>
                        {context.locationName && (
                            <p>Location: <span className="text-foreground">{context.locationName}</span></p>
                        )}
                        {reportType === "SECTION" && context.sectionKey && (
                            <p>Section: <span className="text-foreground capitalize">{context.sectionKey?.replace(/-/g, ' ')}</span></p>
                        )}
                        {reportType === "POST" && context.postId && (
                            <p>Post ID: <span className="text-foreground">{context.postId}</span></p>
                        )}
                    </div>

                    <div className="flex gap-3 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="flex-1"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                    </div>
                </form>
            </SheetContent>
        </Sheet>
    );
}
